import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, HeartHandshake } from "lucide-react";
import AsaLogo from "../components/AsaLogo";
import { getCheckoutStatus, readPending, clearPending } from "../payments";
import { notify } from "../notify";
import { useAsaAuth } from "../AuthContext";
import { saveConsent } from "../phase3";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState("pending"); // pending | success | error
  const [msg, setMsg] = useState("Confirming your payment…");
  const done = useRef(false);
  const pendingRef = useRef(null);
  const { refreshUser } = useAsaAuth();

  useEffect(() => {
    // Capture pending context ONCE, synchronously, before any async/StrictMode re-run can clear it.
    if (pendingRef.current === null) {
      pendingRef.current = readPending();
    }
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setState("error"); setMsg("Missing payment session."); return; }

    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sid);
        if (data.payment_status === "paid") { complete(data); return; }
        if (data.status === "expired") { setState("error"); setMsg("Your payment session expired. Please try again."); return; }
      } catch (e) { console.debug("Payment status poll retry:", e?.message); }
      if (attempts >= 12) { setState("error"); setMsg("We couldn't confirm your payment in time. If you were charged, please contact support."); return; }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = async (data) => {
    if (done.current) return;
    done.current = true;
    const pending = pendingRef.current || readPending();
    clearPending();
    // Backend (the paid Stripe session) is the source of truth for tier/billing/kind.
    const kind = data.kind || pending.kind;
    const tier = data.tier || pending.tier || "essential";
    const billing = data.billing || pending.billing || "monthly";

    if (kind === "register") {
      notify("registration", `New member payment confirmed (${pending.email || data.email}). Tier: ${tier}, billing: ${billing}. Consent to anonymous data sharing: yes.`, pending.email || data.email);
      await refreshUser();
      setState("success"); setMsg("Payment successful — setting up your account…");
      setTimeout(() => navigate("/asa/dashboard"), 1300);
    } else if (kind === "upgrade") {
      notify("upgrade", `Member upgraded to ${tier} (${billing}).`, pending.email);
      await refreshUser();
      setState("success"); setMsg("Upgrade complete — your new features are unlocked!");
      setTimeout(() => navigate(pending.returnTo || "/asa/dashboard"), 1300);
    } else {
      // One-off report purchase — generate & download the actual PDF the user paid for.
      const sessionId = data.session_id || new URLSearchParams(window.location.search).get("session_id");
      setState("pending"); setMsg("Payment received — preparing your report…");
      try {
        const res = await axios.get(`${API}/reports/download/${sessionId}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(pending.title || data.title || "asal-report").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        notify("download", `Report purchased & downloaded: ${pending.title || data.title || "report"}.`, pending.email);
        setState("success"); setMsg("Payment successful — your report has downloaded. Check your files.");
        toast.success(`${pending.title || data.title || "Your report"} downloaded`);
      } catch (err) {
        console.error("Report download failed:", err);
        setState("error");
        setMsg("Your payment succeeded, but we couldn't generate the file automatically. Please contact support and we'll email it to you right away.");
        return;
      }
      setTimeout(() => navigate(pending.returnTo || "/asa/reports"), 2200);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f8f7] flex flex-col items-center justify-center px-4">
      <div className="mb-8"><AsaLogo size={38} /></div>
      <div className="w-full max-w-[440px] bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
        {state === "pending" && <Loader2 size={44} className="text-[#0d9488] mx-auto animate-spin" />}
        {state === "success" && <CheckCircle2 size={44} className="text-[#22a35a] mx-auto" />}
        {state === "error" && <XCircle size={44} className="text-[#e0567a] mx-auto" />}
        <h1 className="font-head font-bold text-slate-800 text-xl mt-4">
          {state === "success" ? "All done!" : state === "error" ? "Something went wrong" : "Processing…"}
        </h1>
        <p className="text-slate-500 text-sm mt-2">{msg}</p>
        {state === "success" && (
          <div className="mt-5 rounded-xl bg-[#e6f4f2] border border-[#cfe9e5] p-3.5 flex items-start gap-2.5 text-left" data-testid="success-charity-note">
            <HeartHandshake size={18} className="text-[#0d9488] mt-0.5 shrink-0" />
            <p className="text-[12.5px] text-[#0f766e] leading-relaxed">Thank you — <strong>100% of your payment funds the ASA Foundation</strong>, helping bring early autoimmune diagnostics to vulnerable people, including children.</p>
          </div>
        )}
        {state === "error" && (
          <button onClick={() => navigate("/asa")} className="mt-5 h-10 px-4 rounded-lg bg-[#0d9488] hover:bg-[#0f766e] text-white font-semibold text-sm transition-colors">Back to home</button>
        )}
      </div>
      <p className="text-[12px] text-slate-400 mt-6">© {new Date().getFullYear()} ASA Foundation · Copyright &amp; IP: Asal Shirazi</p>
    </div>
  );
}
